import { useState, useEffect, useCallback, useRef } from 'react';
import { getPins, searchPins, createPin, resolveImageUrl, getSettings } from '../api/client';

interface Pin {
  id: string;
  sku: string;
  name: string;
  description?: string;
  series?: string;
  tags?: string[];
  moderationStatus: string;
  isVerified: boolean;
  isLimitedEdition: boolean;
  isNoBarcodePin?: boolean;
  isPinGroup?: boolean;
  childPinCount?: number;
  createdAt: string;
  updatedAt?: string;
  officialImageUrl?: string;
  aiGenerated?: {
    isAITagged: boolean;
    aiConfidence?: number;
    lastAIProcessed?: string;
  };
}

function PinImage({ src, name }: { src?: string; name: string }) {
  const [showPreview, setShowPreview] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [previewPos, setPreviewPos] = useState({ x: 0, y: 0 });
  const imgRef = useRef<HTMLImageElement>(null);

  const handleMouseEnter = () => {
    if (imgRef.current) {
      const rect = imgRef.current.getBoundingClientRect();
      setPreviewPos({ x: rect.right + 8, y: rect.top });
    }
    setShowPreview(true);
  };

  if (!src || imageError) {
    return (
      <div className="w-8 h-8 rounded bg-gray-200 flex items-center justify-center text-gray-400 text-sm flex-shrink-0">
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
        </svg>
      </div>
    );
  }

  return (
    <>
      <img
        ref={imgRef}
        src={src}
        alt={name}
        className="w-8 h-8 rounded object-cover flex-shrink-0 cursor-pointer hover:ring-2 hover:ring-blue-400 transition-all"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setShowPreview(false)}
        onError={() => setImageError(true)}
      />
      {showPreview && (
        <div
          className="fixed z-[9999] p-1.5 bg-white rounded-lg shadow-xl border border-gray-200 pointer-events-none"
          style={{ left: previewPos.x, top: previewPos.y }}
        >
          <img
            src={src}
            alt={name}
            className="w-40 h-40 object-contain rounded"
          />
        </div>
      )}
    </>
  );
}

function PinDetailPanel({
  pin,
  onClose,
}: {
  pin: Pin;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-slate-800">Pin Details</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Image */}
          {pin.officialImageUrl && (
            <div className="flex justify-center">
              <img
                src={resolveImageUrl(pin.officialImageUrl)}
                alt={pin.name}
                className="max-h-64 rounded-lg shadow-md object-contain"
              />
            </div>
          )}

          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase">Name</label>
              <div className="text-slate-800 font-medium">{pin.name}</div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase">SKU</label>
              <div className="font-mono text-sm text-gray-600">{pin.sku}</div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase">Status</label>
              <div>
                <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                  pin.moderationStatus === 'approved' ? 'bg-green-100 text-green-700' :
                  pin.moderationStatus === 'ai_approved' ? 'bg-purple-100 text-purple-700' :
                  pin.moderationStatus === 'rejected' ? 'bg-red-100 text-red-700' :
                  'bg-amber-100 text-amber-700'
                }`}>
                  {pin.moderationStatus}
                </span>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase">Created</label>
              <div className="text-sm text-gray-600">{new Date(pin.createdAt).toLocaleString()}</div>
            </div>
            {pin.series && (
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">Series</label>
                <div className="text-sm text-gray-700">{pin.series}</div>
              </div>
            )}
          </div>

          {pin.description && (
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase">Description</label>
              <div className="text-sm text-gray-700">{pin.description}</div>
            </div>
          )}

          {/* Tags */}
          {pin.tags && pin.tags.length > 0 && (
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase">Tags</label>
              <div className="flex flex-wrap gap-1 mt-1">
                {pin.tags.map((tag, i) => (
                  <span key={i} className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Badges */}
          <div className="flex flex-wrap gap-2">
            {pin.isLimitedEdition && (
              <span className="flex items-center gap-1 px-2 py-1 text-xs font-medium bg-amber-100 text-amber-700 rounded">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                Limited Edition
              </span>
            )}
            {pin.isNoBarcodePin && (
              <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded">
                No Barcode
              </span>
            )}
            {pin.isPinGroup && (
              <span className="flex items-center gap-1 px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z" />
                </svg>
                Pin Group ({pin.childPinCount} pins)
              </span>
            )}
            {pin.isVerified && (
              <span className="flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Verified
              </span>
            )}
          </div>

          {/* AI Info */}
          {pin.aiGenerated?.isAITagged && (
            <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                <span className="text-sm font-medium text-purple-800">AI Generated</span>
                {pin.aiGenerated.aiConfidence && (
                  <span className="ml-auto text-xs text-purple-600">
                    {pin.aiGenerated.aiConfidence}% confidence
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer hint */}
        <div className="px-4 py-3 border-t bg-gray-50 text-xs text-gray-500">
          Status changes are managed from the Submissions screen.
        </div>
      </div>
    </div>
  );
}

function AddPinModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [formData, setFormData] = useState({
    sku: '',
    name: '',
    description: '',
    series: '',
    tags: '',
    retailPrice: '',
    isLimitedEdition: false,
    isNoBarcodePin: false,
  });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.sku.trim() || !formData.name.trim()) {
      setFormError('SKU and Name are required');
      return;
    }

    setSaving(true);
    setFormError('');
    try {
      await createPin({
        sku: formData.sku.trim().toUpperCase(),
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        series: formData.series.trim() || undefined,
        tags: formData.tags.trim() ? formData.tags.split(',').map(t => t.trim()).filter(Boolean) : undefined,
        retailPrice: formData.retailPrice ? Number(formData.retailPrice) : undefined,
        isLimitedEdition: formData.isLimitedEdition,
        isNoBarcodePin: formData.isNoBarcodePin,
      });
      onCreated();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to create pin');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-slate-800">Add Pin</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-3">
          {formError && (
            <div className="bg-red-50 text-red-600 px-3 py-2 rounded text-sm">{formError}</div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">SKU *</label>
              <input
                type="text"
                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="e.g. 12345"
                value={formData.sku}
                onChange={(e) => setFormData(prev => ({ ...prev, sku: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Series</label>
              <input
                type="text"
                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="e.g. Hidden Mickey"
                value={formData.series}
                onChange={(e) => setFormData(prev => ({ ...prev, series: e.target.value }))}
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Name *</label>
            <input
              type="text"
              className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="Pin name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
            <textarea
              className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              rows={2}
              placeholder="Optional description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Tags (comma-separated)</label>
              <input
                type="text"
                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="e.g. mickey, classic"
                value={formData.tags}
                onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Retail Price</label>
              <input
                type="number"
                step="0.01"
                min="0"
                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="0.00"
                value={formData.retailPrice}
                onChange={(e) => setFormData(prev => ({ ...prev, retailPrice: e.target.value }))}
              />
            </div>
          </div>

          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={formData.isLimitedEdition}
                onChange={(e) => setFormData(prev => ({ ...prev, isLimitedEdition: e.target.checked }))}
              />
              Limited Edition
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={formData.isNoBarcodePin}
                onChange={(e) => setFormData(prev => ({ ...prev, isNoBarcodePin: e.target.checked }))}
              />
              No Barcode
            </label>
          </div>

          <div className="flex gap-3 pt-2 border-t">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 disabled:opacity-50 transition-colors"
            >
              {saving ? 'Creating...' : 'Create Pin'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Pins() {
  const [pins, setPins] = useState<Pin[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [selectedPin, setSelectedPin] = useState<Pin | null>(null);
  const [showAddPin, setShowAddPin] = useState(false);
  // Mirror of the backend's `aiApprovedPinsRequireModeration` setting so the
  // header can describe which statuses are currently visible. The server still
  // owns the actual filter — this is just a label.
  const [aiSandboxed, setAiSandboxed] = useState<boolean | null>(null);
  const limit = 20;
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Reset when search changes
  useEffect(() => {
    setPins([]);
    setPage(1);
    setHasMore(true);
    setLoading(true);
  }, [debouncedSearch]);

  // Fetch the moderation setting once on mount so the header label is honest.
  useEffect(() => {
    let cancelled = false;
    getSettings()
      .then((res) => {
        if (!cancelled) setAiSandboxed(!!res.data?.aiApprovedPinsRequireModeration);
      })
      .catch(() => {
        if (!cancelled) setAiSandboxed(null);
      });
    return () => { cancelled = true; };
  }, []);

  const fetchPins = useCallback(async (pageNum: number, isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      // No status param -> backend honors the `moderation_sandboxing_enabled` global
      // setting: approved-only when ON, approved + ai_approved when OFF. This keeps
      // the Pins screen identical to what iOS Discover sees.
      const result = debouncedSearch
        ? await searchPins(debouncedSearch, pageNum, limit)
        : await getPins(pageNum, limit);

      const newPins = result.data?.pins as Pin[] || [];
      const totalCount = result.data?.pagination?.total || 0;

      if (isLoadMore) {
        setPins(prev => [...prev, ...newPins]);
      } else {
        setPins(newPins);
      }

      setTotal(totalCount);
      setHasMore(pageNum * limit < totalCount);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load pins');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [debouncedSearch]);

  // Initial load and search changes
  useEffect(() => {
    fetchPins(1);
  }, [fetchPins]);

  // Infinite scroll observer
  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && !loadingMore) {
          const nextPage = page + 1;
          setPage(nextPage);
          fetchPins(nextPage, true);
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, loading, loadingMore, page, fetchPins]);

  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem-2rem)] sm:h-[calc(100vh-3.5rem-3rem)] lg:h-[calc(100vh-4rem)]">
      {/* Compact Header */}
      <div className="flex-shrink-0 bg-gray-100 pb-3">
        <div className="flex items-center justify-between gap-4 mb-3">
          <div className="flex items-baseline gap-3">
            <h1 className="text-xl font-bold text-slate-800">Pins</h1>
            <span className="text-sm text-gray-500">
              {total.toLocaleString()} total
              {pins.length > 0 && pins.length < total && ` / ${pins.length} shown`}
            </span>
          </div>
          <button
            onClick={() => setShowAddPin(true)}
            className="px-3 py-1.5 bg-blue-500 text-white text-sm font-medium rounded hover:bg-blue-600 transition-colors"
          >
            + Add Pin
          </button>
        </div>

        {/* Search */}
        <div className="flex gap-3 items-center">
          <div className="relative flex-1 max-w-md">
            <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              className="w-full pl-8 pr-8 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Search pins..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          <span className="text-xs text-gray-500">
            {aiSandboxed === null
              ? 'Matches iOS Discover'
              : aiSandboxed
                ? 'Showing approved pins only (AI sandbox is ON)'
                : 'Showing approved + AI-approved pins (AI sandbox is OFF)'}
          </span>
        </div>
      </div>

      {error && (
        <div className="flex-shrink-0 bg-red-50 text-red-600 px-3 py-2 rounded text-sm mb-2">
          {error}
        </div>
      )}

      {/* Scrollable Content */}
      <div className="flex-1 overflow-hidden flex flex-col min-h-0">
        {loading && pins.length === 0 ? (
          <div className="flex items-center justify-center p-8 text-gray-500 text-sm">
            Loading pins...
          </div>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="sm:hidden flex-1 overflow-y-auto">
              <div className="space-y-2 pb-4">
                {pins.map((pin) => (
                  <div
                    key={pin.id}
                    className="bg-white rounded shadow-sm p-3 cursor-pointer active:bg-gray-50"
                    onClick={() => setSelectedPin(pin)}
                  >
                    <div className="flex gap-3">
                      <div className="flex-shrink-0">
                        {pin.officialImageUrl ? (
                          <img src={resolveImageUrl(pin.officialImageUrl)} alt={pin.name} className="w-12 h-12 rounded object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                        ) : (
                          <div className="w-12 h-12 rounded bg-gray-200 flex items-center justify-center text-gray-400">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start gap-2">
                          <div className="font-medium text-slate-800 text-sm truncate">{pin.name}</div>
                          {pin.moderationStatus === 'ai_approved' ? (
                            <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-purple-100 text-purple-700">ai</span>
                          ) : (
                            <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-green-100 text-green-700">approved</span>
                          )}
                        </div>
                        <code className="text-xs text-gray-500 font-mono">{pin.sku}</code>
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={loadMoreRef} className="h-8 flex items-center justify-center">
                  {loadingMore && <div className="text-gray-500 text-xs">Loading more...</div>}
                </div>
              </div>
            </div>

            {/* Desktop Table View */}
            <div className="hidden sm:flex flex-col flex-1 bg-white rounded shadow-sm overflow-hidden min-h-0">
              {/* Table Header */}
              <div className="flex-shrink-0 bg-slate-700 text-white text-xs font-semibold uppercase tracking-wide">
                <div className="flex items-center">
                  <div className="w-11 flex-shrink-0 px-2 py-2"></div>
                  <div className="w-36 flex-shrink-0 px-2 py-2">SKU</div>
                  <div className="flex-1 min-w-0 px-2 py-2">Name</div>
                  <div className="w-32 flex-shrink-0 px-2 py-2 hidden lg:block">Series</div>
                  <div className="w-20 flex-shrink-0 px-2 py-2">Status</div>
                  <div className="w-12 flex-shrink-0 px-2 py-2 hidden lg:flex justify-center" title="Verified">
                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Scrollable Table Body */}
              <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
                {pins.map((pin) => (
                  <div
                    key={pin.id}
                    className="flex items-center hover:bg-gray-50 text-sm cursor-pointer"
                    onClick={() => setSelectedPin(pin)}
                  >
                    <div className="w-11 flex-shrink-0 px-2 py-1.5">
                      <PinImage src={resolveImageUrl(pin.officialImageUrl)} name={pin.name} />
                    </div>
                    <div className="w-36 flex-shrink-0 px-2 py-1.5 overflow-hidden">
                      <code className="text-xs bg-gray-100 px-1 py-0.5 rounded font-mono text-gray-600 truncate block">{pin.sku}</code>
                    </div>
                    <div className="flex-1 min-w-0 px-2 py-1.5 text-slate-800 font-medium truncate">{pin.name}</div>
                    <div className="w-32 flex-shrink-0 px-2 py-1.5 text-gray-500 truncate hidden lg:block">{pin.series || '-'}</div>
                    <div className="w-20 flex-shrink-0 px-2 py-1.5">
                      {pin.moderationStatus === 'ai_approved' ? (
                        <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-purple-100 text-purple-700">ai</span>
                      ) : (
                        <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-green-100 text-green-700">approved</span>
                      )}
                    </div>
                    <div className="w-12 flex-shrink-0 px-2 py-1.5 hidden lg:block text-center">
                      {pin.isVerified ? (
                        <svg className="w-4 h-4 text-green-500 inline" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </div>
                  </div>
                ))}

                {/* Load More Trigger */}
                <div ref={loadMoreRef} className="h-10 flex items-center justify-center">
                  {loadingMore && <div className="text-gray-500 text-xs">Loading more...</div>}
                  {!hasMore && pins.length > 0 && <div className="text-gray-400 text-xs">End of list</div>}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Detail Panel Modal */}
      {selectedPin && (
        <PinDetailPanel
          pin={selectedPin}
          onClose={() => setSelectedPin(null)}
        />
      )}

      {/* Add Pin Modal */}
      {showAddPin && (
        <AddPinModal
          onClose={() => setShowAddPin(false)}
          onCreated={() => {
            setShowAddPin(false);
            setPins([]);
            setPage(1);
            setHasMore(true);
            fetchPins(1);
          }}
        />
      )}
    </div>
  );
}
